/* Платформа Mini App (00-architecture §1, §5): Telegram, MAX или обычный браузер (разработка и проверки).
   React зовёт только этот слой: закрыть окно, виброотклик, кнопка «Назад», данные запуска для входа на бэкенде.
   [ТЕЛЕГРАМ] — telegram-web-app.js (window.Telegram.WebApp, подключается в production-сборке).
   [MAX] max-web-app.js (window.WebApp) подключается, когда MAX передал данные запуска (#WebAppData=…); ready и close
   в документации моста не описаны — вызываются, только если есть (docs/65 §3). */

import { runtimePreferences } from '../features/settings/runtime-preferences';
import { installSafeArea } from './safe-area';
export type PlatformKind = 'telegram' | 'max' | 'browser';
export type Haptic = 'light' | 'medium' | 'heavy' | 'success' | 'error' | 'warning';

export interface Platform {
  kind: PlatformKind;
  /** Сырые данные запуска: бэкенд проверяет подпись и выдаёт сессию (POST /app/auth). Вне Mini App — пусто */
  initData(): string;
  /** start_param ссылки запуска (gift_…, DA_w_…, r_…) */
  startParam(): string;
  haptic(kind: Haptic): void;
  /** Закрыть Mini App (после выбора голоса на вкладке «Озвучка», 00-architecture §5) */
  close(): void;
  ready(): void;
  backButton(show: boolean, onClick?: () => void): void;
}

interface MaxBridge {
  initData?: string;
  initDataUnsafe?: { start_param?: string };
  ready?(): void;
  close?(): void;
  HapticFeedback?: TelegramWebApp['HapticFeedback'];
  BackButton?: TelegramWebApp['BackButton'];
}

/* [ТЕЛЕГРАМ] Без expand() приложение открывается на часть высоты. Весь экран без шапки Telegram (Bot API 8.0+) — на любом клиенте:
   30.10.2026 владелец попросил и на компьютере тот же вид, что на телефоне (кнопки Telegram поверх, «чёлка»), — так удобнее проверять.
   На компьютере окно мини-аппа разворачивается на весь монитор; выйти — Esc или кнопкой Telegram.
   Если BotFather уже открыл приложение на весь экран, запрос ничего не меняет. Отступы под кнопки Telegram и системную панель — safe-area.ts */
function openFull(tg: TelegramWebApp) {
  let full = false;
  try {
    tg.expand();
    const at = (v: string) => tg.isVersionAtLeast?.(v) === true;
    /* браузерный Telegram (web, weba, webk) — без полного экрана: он развернул бы вкладку браузера */
    const os = tg.platform ?? 'unknown';
    full = at('8.0') && os !== 'unknown' && !os.startsWith('web');
    if (full && !tg.isFullscreen) tg.requestFullscreen?.();
    if (at('7.7')) tg.disableVerticalSwipes?.();
  } catch {
    /* старые клиенты без этих методов */
  }
  if (tg.isVersionAtLeast?.('8.0')) installSafeArea(tg, full);
}

function fromWebApp(kind: PlatformKind, w: TelegramWebApp | MaxBridge): Platform {
  let back: (() => void) | null = null;
  return {
    kind,
    initData: () => w.initData || '',
    startParam: () =>
      w.initDataUnsafe?.start_param ||
      new URLSearchParams(location.search).get('tgWebAppStartParam') ||
      new URLSearchParams(location.search).get('startapp') ||
      '',
    haptic(k) {
      if (runtimePreferences()?.vibration === false) return;
      const h = w.HapticFeedback;
      if (!h) return;
      try {
        if (k === 'success' || k === 'error' || k === 'warning') h.notificationOccurred(k);
        else h.impactOccurred(k);
      } catch {
        /* старые клиенты без виброотклика */
      }
    },
    close: () => w.close?.(),
    ready() {
      w.ready?.();
      if (kind === 'telegram') openFull(w as TelegramWebApp);
    },
    backButton(show, onClick) {
      const b = w.BackButton;
      if (!b) return;
      if (onClick && onClick !== back) {
        back = onClick;
        b.onClick(() => back?.());
      }
      if (show) b.show();
      else b.hide();
    },
  };
}

const browser: Platform = {
  kind: 'browser',
  initData: () => '',
  startParam: () => new URLSearchParams(location.search).get('startapp') || '',
  haptic(k) {
    if (runtimePreferences()?.vibration === false) return;
    if (k === 'medium' || k === 'heavy') navigator.vibrate?.(24);
  },
  close() {
    /* [ДЕМО] вне Mini App окно не закрывается: прототип показывает условный чат (app.html collapseApp) */
    document.dispatchEvent(new CustomEvent('bot:close'));
  },
  ready() {},
  backButton() {},
};

function detect(): Platform {
  const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;
  if (tg && tg.initData) return fromWebApp('telegram', tg);
  const max = typeof window !== 'undefined' ? (window.WebApp as MaxBridge | undefined) : undefined;
  if (max && max.initData) return fromWebApp('max', max);
  return browser;
}

export const platform: Platform = detect();
